/**
 * Gestão de Acessos da Equipe (somente Gestor Master).
 *
 * Permite criar, editar, bloquear, resetar senha e excluir (soft-delete)
 * usuários da EQUIPE — Gestor Master, Operador de Eventos, Comercial.
 *
 * Alunos comuns continuam sendo gerenciados em /admin/usuarios.
 */
import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Shield, ShieldOff, Trash2, RefreshCcw, KeyRound,
  Copy, Eye, EyeOff, AlertTriangle, Lock, Sparkles, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const STAFF_ROLES = [
  { value: "admin_master", label: "Gestor Master", description: "Acesso total ao sistema, gerencia usuários e permissões", color: "bg-purple-100 text-purple-800 border-purple-200" },
  { value: "operador_evento", label: "Operador de Eventos", description: "Acesso apenas à leitura de QR Code e check-in/check-out", color: "bg-sky-100 text-sky-800 border-sky-200" },
  { value: "comercial", label: "Comercial", description: "Visualiza inscrições/leads e relatórios comerciais", color: "bg-amber-100 text-amber-800 border-amber-200" },
];

const roleInfo = (role) => STAFF_ROLES.find((r) => r.value === role) || { label: role, color: "bg-gray-100 text-gray-700 border-gray-200" };

function CredentialsCard({ data, onClose }) {
  const [showPwd, setShowPwd] = useState(false);
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };
  return (
    <Card className="bg-amber-50 border-amber-300 border-2">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Credenciais geradas — copie agora</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Esta é a <strong>única</strong> vez que a senha aparecerá. Envie ao usuário por canal seguro (WhatsApp pessoal ou em mãos). No primeiro acesso, ele será obrigado a trocá-la.
            </p>
          </div>
        </div>
        <div className="space-y-2 bg-white rounded-lg p-3 border border-amber-200">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground font-medium tracking-wider">Email</p>
              <p className="font-mono text-sm">{data.email}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => copy(data.email, "E-mail")}>
              <Copy className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground font-medium tracking-wider">Senha temporária</p>
              <p className="font-mono text-sm">{showPwd ? data.password : "•".repeat(data.password?.length || 10)}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowPwd((s) => !s)}>
              {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </Button>
            <Button size="sm" variant="outline" onClick={() => copy(data.password, "Senha")}>
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
        <Button onClick={onClose} className="w-full" data-testid="creds-confirm">
          Já copiei — entendi que não verei novamente
        </Button>
      </CardContent>
    </Card>
  );
}

function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    role: "operador_evento",
    password_mode: "auto", // auto | manual
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null);

  const submit = async () => {
    if (form.full_name.trim().length < 2) {
      toast.error("Informe o nome completo");
      return;
    }
    if (!form.email.includes("@")) {
      toast.error("E-mail inválido");
      return;
    }
    if (form.password_mode === "manual" && form.password.length < 6) {
      toast.error("Senha manual precisa ter pelo menos 6 caracteres");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
      };
      if (form.password_mode === "manual") payload.password = form.password;
      const { data } = await api.post("/admin/users", payload);
      toast.success("Usuário criado");
      if (data.temp_password) {
        setCreds({ email: data.user.email, password: data.temp_password });
      } else {
        // senha definida manualmente
        setCreds({ email: data.user.email, password: form.password });
      }
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar usuário");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm({ full_name: "", email: "", role: "operador_evento", password_mode: "auto", password: "" });
    setCreds(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Criar usuário da equipe</DialogTitle>
          <DialogDescription>
            Defina o perfil de acesso. O usuário receberá uma senha temporária e será obrigado a trocá-la no primeiro acesso.
          </DialogDescription>
        </DialogHeader>
        {creds ? (
          <CredentialsCard data={creds} onClose={() => { reset(); onClose(); }} />
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="full_name">Nome completo *</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                placeholder="Ex: João da Silva"
                data-testid="create-user-name"
              />
            </div>
            <div>
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="joao@ceapbrasil.com"
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label htmlFor="role">Perfil de acesso *</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAFF_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                {STAFF_ROLES.find((r) => r.value === form.role)?.description}
              </p>
            </div>
            <div>
              <Label>Senha</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, password_mode: "auto" }))}
                  className={`p-2 rounded-md border-2 text-xs ${form.password_mode === "auto" ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10" : "border-[hsl(var(--border))]"}`}
                >
                  Gerar automática
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, password_mode: "manual" }))}
                  className={`p-2 rounded-md border-2 text-xs ${form.password_mode === "manual" ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10" : "border-[hsl(var(--border))]"}`}
                >
                  Definir manualmente
                </button>
              </div>
              {form.password_mode === "manual" && (
                <Input
                  className="mt-2"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="create-user-password"
                />
              )}
            </div>
            <Button onClick={submit} disabled={saving} className="w-full" data-testid="create-user-submit">
              {saving ? "Criando…" : "Criar usuário"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, open, onClose }) {
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);

  const doReset = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
      setCreds({ email: data.email, password: data.temp_password });
      toast.success("Senha redefinida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setCreds(null); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resetar senha</DialogTitle>
          <DialogDescription>
            Vai gerar uma senha temporária forte para <strong>{user?.full_name}</strong>. A senha aparecerá UMA ÚNICA VEZ.
          </DialogDescription>
        </DialogHeader>
        {creds ? (
          <CredentialsCard data={creds} onClose={() => { setCreds(null); onClose(); }} />
        ) : (
          <div className="space-y-3">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>A senha atual será invalidada IMEDIATAMENTE. Se este usuário estiver logado, será desconectado no próximo refresh.</p>
              </CardContent>
            </Card>
            <Button onClick={doReset} disabled={loading} className="w-full" variant="destructive" data-testid="reset-pwd-confirm">
              <KeyRound className="w-4 h-4 mr-1.5" />
              {loading ? "Gerando…" : "Confirmar — gerar nova senha"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminGestaoAcessos() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/users", { params: { staff_only: true, include_deleted: includeDeleted } })
      .then((r) => setUsers(r.data || []))
      .catch(() => toast.error("Erro ao carregar usuários"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeDeleted]);

  const updateRole = async (u, newRole) => {
    if (!window.confirm(`Alterar perfil de "${u.full_name}" para "${roleInfo(newRole).label}"?`)) return;
    try {
      await api.put(`/admin/users/${u.id}`, { role: newRole });
      toast.success("Perfil atualizado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const toggleBlock = async (u) => {
    if (u.id === me?.id) {
      toast.error("Você não pode bloquear sua própria conta.");
      return;
    }
    const action = u.is_blocked ? "Desbloquear" : "Bloquear";
    const reason = u.is_blocked ? null : window.prompt("Motivo do bloqueio (opcional):") || null;
    if (!window.confirm(`${action} "${u.full_name}"?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/block`, { blocked: !u.is_blocked, reason });
      toast.success(`Conta ${u.is_blocked ? "desbloqueada" : "bloqueada"}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const softDelete = async (u) => {
    if (u.id === me?.id) {
      toast.error("Você não pode excluir sua própria conta.");
      return;
    }
    if (!window.confirm(`Excluir "${u.full_name}"?\n\nA conta será desativada e perderá acesso imediatamente. O histórico (audit log, ações antigas) será preservado para auditoria.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Conta desativada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const restore = async (u) => {
    if (!window.confirm(`Restaurar "${u.full_name}"? O usuário voltará a poder acessar o sistema com a última senha registrada.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/restore`);
      toast.success("Conta restaurada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const groupedByRole = STAFF_ROLES.map((r) => ({
    ...r,
    users: users.filter((u) => u.role === r.value),
  }));

  return (
    <AdminLayout title="Gestão de Acessos">
      <div className="space-y-4">
        {/* Aviso de segurança */}
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-purple-900">Você está no módulo de Gestão de Acessos</p>
              <p className="text-xs text-purple-800 mt-1">
                Somente <strong>Gestores Master</strong> têm acesso a esta tela. Toda ação (criação, alteração, exclusão, reset de senha) é registrada no log de auditoria com seu nome e data.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setIncludeDeleted((v) => !v)} data-testid="toggle-deleted">
              {includeDeleted ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
              {includeDeleted ? "Ocultar desativados" : "Mostrar desativados"}
            </Button>
            <Button size="sm" variant="ghost" onClick={load}>
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Atualizar
            </Button>
          </div>
          <Button onClick={() => setOpenCreate(true)} data-testid="create-staff-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Criar usuário
          </Button>
        </div>

        {/* Resumo por perfil */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {groupedByRole.map((g) => (
            <Card key={g.value} className="rounded-xl">
              <CardContent className="p-4">
                <Badge variant="outline" className={`${g.color} mb-2`}>{g.label}</Badge>
                <p className="font-display text-2xl font-semibold">{g.users.length}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{g.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabela */}
        <Card className="rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="staff-users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último acesso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-8">Carregando…</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">Nenhum usuário</TableCell></TableRow>
                ) : (
                  users.map((u) => {
                    const ri = roleInfo(u.role);
                    const isSelf = u.id === me?.id;
                    const deleted = !!u.deleted_at;
                    return (
                      <TableRow key={u.id} className={deleted ? "opacity-60" : ""}>
                        <TableCell>
                          <div className="font-medium text-sm">
                            {u.full_name}
                            {isSelf && <Badge variant="outline" className="ml-1 text-[9px]">Você</Badge>}
                          </div>
                          <p className="text-[10px] text-muted-foreground font-mono">{u.ceap_id}</p>
                        </TableCell>
                        <TableCell className="text-xs">{u.email}</TableCell>
                        <TableCell>
                          {deleted || isSelf ? (
                            <Badge variant="outline" className={ri.color}>{ri.label}</Badge>
                          ) : (
                            <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                              <SelectTrigger className="w-[160px] h-7 text-xs" data-testid={`role-select-${u.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STAFF_ROLES.map((r) => (
                                  <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          {deleted ? (
                            <Badge className="bg-gray-200 text-gray-700 border-gray-300 text-[10px]">Desativado</Badge>
                          ) : u.is_blocked ? (
                            <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">Bloqueado</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Ativo</Badge>
                          )}
                          {u.must_change_password && (
                            <Badge variant="outline" className="ml-1 text-[9px] bg-amber-50">precisa trocar senha</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {u.last_login_at ? new Date(u.last_login_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "Nunca"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            {!deleted && (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setResetTarget(u)} title="Resetar senha" data-testid={`reset-${u.id}`}>
                                  <KeyRound className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleBlock(u)} title={u.is_blocked ? "Desbloquear" : "Bloquear"} disabled={isSelf} data-testid={`block-${u.id}`}>
                                  {u.is_blocked ? <Shield className="w-3.5 h-3.5 text-emerald-600" /> : <ShieldOff className="w-3.5 h-3.5 text-rose-600" />}
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => softDelete(u)} title="Excluir" disabled={isSelf} data-testid={`delete-${u.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            {deleted && (
                              <Button size="sm" variant="ghost" className="h-7" onClick={() => restore(u)} data-testid={`restore-${u.id}`}>
                                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <CreateUserDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={load}
      />
      {resetTarget && (
        <ResetPasswordDialog
          user={resetTarget}
          open={!!resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </AdminLayout>
  );
}
